import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  getIncomingAuthorizeParams, 
  getOrCreateRequestId,
  parseResponseUrlQuery,
  persistAppUrl,
} from '../../reducers/auth/authAuthorizeHelpers';
import { authorizeRequest, getCustomerAppInfoRequest } from '../../backend/authRequests';
import { to } from '../../lib/requestHelpers';
import { extractAgencyId } from '../../reducers/auth/authAuthorizeHelpers';
import { updateAuthState, AUTH_SERVER_ERROR_MODAL_OPEN, AUTH_SET_USER_DETAILS, startAuthorizeTimer } from '../../reducers/auth/authActions';

function Authorize() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const params = getIncomingAuthorizeParams();
    // Extract client_id from incoming authorize params
    const clientId = params?.client_id;
    const agencyId = extractAgencyId(clientId);

    if (!clientId) {
      navigate('/sign-in', { replace: true });
      return;
    }

    const requestId = getOrCreateRequestId();
    dispatch(
      updateAuthState({
        requestId,
        clientId,
        agencyId,
        authType: 'oauth',
      }),
    );

    const runAuthorize = async () => {
      const [result, err] = await to(dispatch(authorizeRequest({ params, forwardedHost: window.location.host, agencyId })));

      const responseBody = result?.body || {};
      const responseStatus = result?.response?.status;
      const location = responseBody.location;
      const locationParams = location ? parseResponseUrlQuery(location) : {};
      const userAliasId = responseBody.userAliasId ?? null;
      const firstName = responseBody.firstName ?? '';
      const lastName = responseBody.lastName ?? '';
      const userEmail = responseBody.userEmail ?? '';
      const phoneNumber = responseBody.phoneNumber ?? '';
      const address = responseBody.address ?? '';
      const hasExistingSession = Boolean(locationParams.code);

      if (responseStatus === 200) {
        // Start 2-minute timer if code is present
        if (hasExistingSession) {
          startAuthorizeTimer(dispatch);
        }
        const [customerAppInfoResult, customerAppInfoErr] = await to(
          dispatch(
            getCustomerAppInfoRequest({
              agencyId,
              uniqueIdentifier: requestId,
              customerAppClientId: clientId,
              forwardedHost: window.location.host,
            })
          )
        );

        const customerAppInfoStatus = customerAppInfoResult?.response?.status;
        const customerAppInfoBody = customerAppInfoResult?.body || {};

        if (customerAppInfoStatus === 200) {
          const appUrl = customerAppInfoBody.appUrl ?? null;
          const appName = customerAppInfoBody.appName ?? null;
          const customerAppId = customerAppInfoBody.customerAppId ?? null;
          // Only persist appUrl if we have a valid clientId from incoming params
          // This prevents re-persisting when user hits back button
          if (clientId && appUrl) {
            persistAppUrl(appUrl);
          }

          dispatch(
            updateAuthState({
              authorizeResponse: {
                location,
                ...locationParams,
              },
              userAliasId,
              appUrl,
              appName,
              customerAppId,
              agencyId,
              hasExistingSession,
              utmParams: {
                utm_source: customerAppInfoBody.utmSource ?? '',
                utm_medium: customerAppInfoBody.utmMedium ?? '',
                utm_campaign: customerAppInfoBody.utmCampaign ?? '',
              },
            }),
          );

          // Store user details from authorize response
          dispatch({
            type: AUTH_SET_USER_DETAILS,
            payload: {
              firstname: firstName,
              lastname: lastName,
              userEmail: userEmail,
              phoneNumber,
              address,
            },
          });
          
          if (location) {
            if (locationParams.code) {
              navigate('/continue-as', { replace: true });
              return;
            }
          }

          // No code in location — proceed to sign-in flow
          navigate('/sign-in', { replace: true });
        } else {
          dispatch({
            type: AUTH_SERVER_ERROR_MODAL_OPEN,
            payload: {
              uniqueIdentifier: requestId,
            },
          });
        }
      } else {
        dispatch({
          type: AUTH_SERVER_ERROR_MODAL_OPEN,
          payload: {
            uniqueIdentifier: requestId,
          },
        });
      }
    };

    runAuthorize();
  }, [dispatch, navigate]);

  return (
    <div className="sign-in-page">
      <div className="sign-in-card">
        <div className="sign-in-card-body">
          <div className="sign-in-loading" role="status" aria-label="Loading" />
        </div>
      </div>
    </div>
  );
}

export default Authorize;

